import React from 'react'
import { Box, Typography, useMediaQuery } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { AUTH_BRAND_COPY, type AuthBrandScreen } from './authBrandCopy'
import AuthLightSurface from './AuthLightSurface'

/**
 * The orange brand panel (SC-438). Its colours come from the workspace theme's primary palette,
 * so a re-branded workspace re-colours it.
 */
export function AuthBrandPanel({ screen }: { screen: AuthBrandScreen }) {
  const theme = useTheme()
  const copy = AUTH_BRAND_COPY[screen]
  const contrast = theme.palette.primary.contrastText
  return (
    <Box
      data-testid={`auth-brand-panel-${screen}`}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: 5.5,
        py: 6,
        color: contrast,
        background: `linear-gradient(160deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      }}
    >
      {/* `inherit`: the theme's Typography variants carry their own text colour, which is dark on the
          light card around this panel. */}
      <Typography component="h2" color="inherit" sx={{ fontSize: { md: 30, lg: 34 }, fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
        {copy.title}
      </Typography>
      <Typography color="inherit" sx={{ fontSize: 16, opacity: 0.9, mb: 3.5 }}>{copy.subtitle}</Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2.25, position: 'relative', zIndex: 1 }}>
        {copy.points.map(({ icon: Icon, text }) => (
          <Box component="li" key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.75, fontSize: 15 }}>
            <Icon sx={{ fontSize: 20, flexShrink: 0 }} aria-hidden />
            <span>{text}</span>
          </Box>
        ))}
      </Box>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 340,
          height: 340,
          borderRadius: '50%',
          left: -70,
          bottom: -150,
          bgcolor: alpha(contrast, 0.1),
        }}
      />
    </Box>
  )
}

/**
 * A full-screen page with the form on the left and the brand panel on the right, as the
 * sign-in dialog lays out (SC-438). The page scrolls itself, because the app shell never
 * scrolls. Below the `md` breakpoint the panel is left out and the form takes the width.
 *
 * The card is always light, like the sign-in dialog, in either workspace mode; the backdrop
 * around it follows the app theme.
 */
export default function AuthSplitCard({
  screen,
  children,
}: {
  screen: AuthBrandScreen
  children: React.ReactNode
}) {
  const theme = useTheme()
  const showPanel = useMediaQuery(theme.breakpoints.up('md'))
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        overflowY: 'auto',
        display: 'flex',
        p: { xs: 2, sm: 3 },
        bgcolor: 'background.default',
      }}
    >
      <AuthLightSurface>
        <Box
          component={motion.div}
          data-testid="auth-split-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            m: 'auto',
            width: '100%',
            maxWidth: showPanel ? 1000 : 500,
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: showPanel ? '1fr 1fr' : '1fr',
            bgcolor: 'background.paper',
            color: 'text.primary',
            backgroundImage: 'none',
            borderRadius: '24px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.shadows[10],
            overflow: 'hidden',
            minHeight: showPanel ? 560 : undefined,
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {children}
          </Box>
          {showPanel && <AuthBrandPanel screen={screen} />}
        </Box>
      </AuthLightSurface>
    </Box>
  )
}
